import type { ChangeEvent } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Icon, Loader, Menu, Text, TextInput, Popup, Sheet } from '@gravity-ui/uikit';

import { EstablishmentAddInput } from './input-add-establishment';
import { useMediaQuery } from 'react-responsive';
import type { AutocompleteArgs } from './types';
import { CirclePlus, Xmark } from '@gravity-ui/icons';
import styles from './styles.module.scss';

/** Компонента для автокоплитов.
 * - Опционально принимает в себя Generic, который передается в необязательный параметр data в атрибуте item
 * - Принимает все атрибуты от TextInput из gravity-ui/uikit
 */
export const Autocomplete = ({
	items,
	isLoading,
	minChar,
	selectedItem,
	onItemSelect,
	onValueChange,
	...rest
}: AutocompleteArgs) => {
	const { value, onChange } = rest;

	const inputRef = useRef<HTMLSpanElement>(null);

	const [isShowPopup, setIsShowPopup] = useState<boolean>(false);

	const [open, setOpen] = useState(false);

	const isMobile = useMediaQuery({ query: '(max-width: 515px)' });

	const menuItems = useMemo(() => {
		return items.map((item, index) => (
			<Menu.Item
				key={item.label + index}
				active={item.value === selectedItem?.value}
				className={styles['autocomplete__menu-item']}
				onClick={() => {
					setIsShowPopup(false);
					onItemSelect(item);
					onValueChange(item.label);
				}}
			>
				{item.label}
			</Menu.Item>
		));
	}, [items, selectedItem?.value, onItemSelect, onValueChange]);

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			onValueChange(e.target.value);
			onItemSelect(undefined);
		},
		[onValueChange, onItemSelect],
	);

	const content = useMemo(() => {
		//Если загрузка, то показываем лоадер
		if (isLoading) {
			return <Loader className={styles['autocomplete__loader']} />;
		}

		//Если количество символов меньше указанного, то показываем заглушку
		if (value !== undefined && minChar !== undefined && value.length < minChar) {
			return (
				<div className={styles['autocomplete__min-char-text']}>
					<Text variant="body-2">
						Начните вводить для поиска
						<br />
						Минимальная длина поиска {minChar} символа
					</Text>
				</div>
			);
		}

		//Если количество записей 0 и передано значение
		if (value !== undefined && value !== '' && !isLoading && items.length === 0) {
			return (
				<div className={styles['autocomplete__add-istitutions-block']}>
					<div className={styles['autocomplete__min-char-text']}>
						<Text variant="body-2">{`По запросу «${value}» ничего не найдено`}</Text>
					</div>

					{open ? (
						<EstablishmentAddInput
							open={open}
							setOpen={setOpen}
							onAdded={(newName: string) => {
								setOpen(false);
								setIsShowPopup(false);
								onValueChange?.(newName);
								onItemSelect?.({ label: newName, value: newName });
							}}
						/>
					) : (
						''
					)}

					<Button size="l" view="outlined" onClick={() => setOpen(true)}>
						<Icon data={CirclePlus} size={18} />
						Добавить учреждение
					</Button>
				</div>
			);
		}

		return <Menu>{menuItems}</Menu>;
	}, [isLoading, minChar, value, menuItems, items.length, open]);

	return (
		<div>
			{isMobile ? (
				<>
					<TextInput
						{...rest}
						onFocus={() => setIsShowPopup(true)}
						hasClear
						ref={inputRef}
						onChange={handleChange}
					/>
					<Sheet
						visible={isShowPopup}
						onClose={() => setIsShowPopup(false)}
						className={styles['sheet']}
						title="Учреждение"
					>
						<div className={styles['sheet__close']}>
							<Button onClick={() => setIsShowPopup(false)} view="flat">
								<Icon data={Xmark} size={16} />
							</Button>
						</div>
						<div className={styles['sheet__content']}>
							<TextInput {...rest} onChange={handleChange} hasClear />
							{content}
						</div>
					</Sheet>
				</>
			) : (
				<>
					<TextInput
						{...rest}
						ref={inputRef}
						onFocus={() => setIsShowPopup(true)}
						onChange={handleChange}
						hasClear
					/>

					<Popup
						open={isShowPopup}
						anchorRef={inputRef}
						placement="bottom"
						contentClassName={styles['autocomplete__popup']}
						style={{ width: inputRef?.current?.clientWidth }}
						onClose={() => setIsShowPopup(false)}
					>
						{content}
					</Popup>
				</>
			)}
		</div>
	);
};

export default Autocomplete;
